import { useEffect, useState } from "react";
import { Table, Button, Input, Typography } from "antd";
import { Row, Col, Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Avatar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import {
  getClients,
  setPagination,
  getFilterCounts,
} from "../../redux/Slices/clientSlice";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import appInsights from "../../AppInsights";

const { Text } = Typography;

const Client = () => {
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profileData } = useSelector((state) => state.profile);
  const { clients, totalCount, currentPage, pageSize, loading, filterCounts } =
    useSelector((state) => state.clients);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTimeout, setSearchTimeout] = useState(null);

  const coachId = profileData?.coachId;

  // Page View
  useEffect(() => {
    if (coachId) {
      appInsights.trackPageView({
        name: "Client_Landing_Screen",
        properties: {
          coachId,
        },
      });
    }
  }, [coachId]);

  useEffect(() => {
    if (coachId) {
      dispatch(getClients({ coachId, sortOrder, page: currentPage, pageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, coachId]);

  useEffect(() => {
    if (coachId) {
      dispatch(getFilterCounts({ coachId }));
    }
  }, [dispatch, coachId]);

  useEffect(() => {
    setFilteredData(clients);
  }, [clients]);

  useEffect(() => {
    if (filterCounts) {
      const formattedFilters = Object.entries(filterCounts).map(
        ([key, count]) => {
          const filterNames = {
            newMessages: "NewMessages",
            newCheckIns: "NewCheckIns",
            reminders: "Reminders",
            followUps: "FollowUps",
            expireSoon: "ExpiresSoon",
            paymentError: "PaymentErrors",
            starting: "starting",
            oldChats: "OldChats",
            inactive: "Inactive",
          };

          return { name: filterNames[key] || key, count };
        }
      );

      setFilters(formattedFilters);
    }
  }, [filterCounts]);

  const sortMenuItems = [
    { key: "desc", label: trans("Common.newest") },
    { key: "asc", label: trans("Common.oldest") },
  ];

  const handleClientNameClick = (client) => {
    appInsights.trackEvent({
      name: "Client_Landing_Screen_Row_Clicked",
      properties: {
        coachId,
        clientId: client?.id,
        hasConversation: !!client?.conversationId,
      },
    });

    navigate("/clients/clientDetails", {
      state: {
        client: client?.id,
        clientDetail: client,
        type: "",
        isConversationEnabled: client?.conversationId ? true : false,
        appliedFilter: selectedFilter?.name || null,
      },
    });
  };

  // Table columns
  const columns = [
    {
      title: "",
      dataIndex: "profileURL",
      key: "profileURL",
      render: (profileURL, record) => (
        <Avatar
          alt={
            record.displayName?.charAt(0)?.toUpperCase() +
            record.displayName?.slice(1)?.toLowerCase()
          }
          src={profileURL}
          className="user-avatar-logo"
        />
      ),
    },
    {
      title: trans("Common.name"),
      dataIndex: "displayName",
      key: "displayName",
      render: (_, record) => (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <span
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => handleClientNameClick(record)}
        >
          {record.firstName?.charAt(0)?.toUpperCase() +
            record.firstName?.slice(1)?.toLowerCase() +
            " " +
            record.lastName?.charAt(0)?.toUpperCase() +
            record.lastName?.slice(1)?.toLowerCase()}
        </span>
      ),
    },
    {
      title: trans("Client.messages"),
      dataIndex: "updates",
      key: "updates",
      render: (count) => {
        return (
          <span className="position-relative d-inline-block">
            <i className="bi bi-messenger icon-blue-colour fs-3"></i>
            {count > 0 && (
              <span className="position-absolute top-1 start-100 translate-middle p-1 bg-danger text-white rounded-circle d-flex justify-content-center align-items-center badge">
                {count}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: trans("Client.checkIns"),
      dataIndex: "unreadCheckInCount",
      key: "unreadCheckInCount",
      render: (count) => {
        return (
          <span className="position-relative d-inline-block">
            <i className="bi icon-blue-colour bi-clipboard-check-fill fs-3"></i>{" "}
            {count > 0 && (
              <span className="position-absolute top-1 start-100 translate-middle p-1 bg-danger text-white rounded-circle d-flex justify-content-center align-items-center badge">
                {count}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: trans("Client.lastContacted"),
      dataIndex: "lastContacted",
      key: "lastContacted",
    },
    {
      title: trans("Common.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let backgroundClass = "";
        let text = status;

        switch (status) {
          case "Active":
            backgroundClass = "bg-active";
            text = "Active";
            break;
          case "Inactive":
            backgroundClass = "bg-inactive";
            text = "Inactive";
            break;
          case "NotSetup":
            backgroundClass = "bg-invalid";
            text = "Not Setup";
            break;
          default:
            backgroundClass = "bg-invalid";
            text = status;
            break;
        }

        return (
          <span
            className={`status-badge ${backgroundClass} d-flex justify-content-center`}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: trans("Client.mealPlan"),
      dataIndex: "mealPlan",
      key: "mealPlan",
    },
    {
      title: trans("Client.workoutPlan"),
      dataIndex: "workoutPlan",
      key: "workoutPlan",
    },
    {
      title: trans("Client.periodToDate"),
      dataIndex: "periodToDate",
      key: "periodToDate",
      render: (_, record) => {
        return (
          <span>
            {record.subscriptionStartDate || "-"} - <br />
            {record.subscriptionEndDate || "-"}
          </span>
        );
      },
    },
    {
      title: trans("Client.paymentStatus"),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
    },
  ];

  // Handle pagination changes
  const handlePageChange = (pagination) => {
    const { current, pageSize } = pagination;
    const updatedFilter = selectedFilter ? selectedFilter?.name : null;

    appInsights.trackEvent({
      name: "Client_Landing_Screen_Pagination_Changed",
      properties: {
        coachId,
        page: current,
        pageSize,
      },
    });

    dispatch(setPagination({ page: current, pageSize }));
    dispatch(
      getClients({
        coachId,
        sortOrder,
        page: current,
        pageSize,
        filter: updatedFilter,
        searchKey: searchQuery,
      })
    );
  };

  const handleFilterClick = (filter) => {
    const isSameFilterSelected = selectedFilter?.name === filter?.name;
    const updatedFilter = isSameFilterSelected ? null : filter;

      appInsights.trackEvent({
      name: "Client_Landing_Screen_Filter_Clicked",
      properties: {
        coachId,
        filter: updatedFilter?.name || "cleared",
      },
    });

    setSelectedFilter(updatedFilter);

    // Reset pagination and call the API with the updated filter
    dispatch(setPagination({ page: 1, pageSize }));
    dispatch(
      getClients({
        coachId,
        sortOrder,
        page: 1,
        pageSize,
        filter: updatedFilter?.name || null,
        searchKey: searchQuery,
      })
    );
  };

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    const updatedFilter = selectedFilter ? selectedFilter?.name : null;

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set a new timeout
    const timeoutId = setTimeout(() => {
      if (query.length >= 3 || query.length === 0) {
        dispatch(setPagination({ page: 1, pageSize }));
        dispatch(
          getClients({
            coachId,
            sortOrder,
            page: 1,
            pageSize,
            filter: updatedFilter,
            searchKey: query,
          })
        );
      }
    }, 2000);

    setSearchTimeout(timeoutId);
  };

  // Handle Sorting
  const handleSortChange = ({ key }) => {
    const updatedFilter = selectedFilter ? selectedFilter?.name : null;
    setSortOrder(key);
    dispatch(setPagination({ page: 1, pageSize }));
    dispatch(
      getClients({
        coachId,
        sortOrder: key,
        page: 1,
        pageSize,
        filter: updatedFilter,
        searchKey: searchQuery,
      })
    );
  };

  // Showing text for pagination
  const getShowingText = () => {
    if (filteredData.length <= 1) return "";
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalCount);
    return `${trans("Common.showing")} ${start} ${trans(
      "Common.to"
    )} ${end} ${trans("Common.of")} ${totalCount} ${trans("Common.results")}`;
  };

  // // Toast notification logic
  // useEffect(() => {
  //   if (error) {
  //     // Trigger success toast
  //     setToastConfig({
  //       type: "error",
  //       message: error,
  //       heading: trans("Common.failed"),
  //     });
  //   }
  // }, [error, trans]);

  const formatFilterName = (str) => {
    // Special case for CheckIns
    if (str.includes("CheckIns")) {
      return str.replace("New", "New ").replace("CheckIns", "CheckIns");
    }
    // Default behavior for others
    return str
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <div className=" formContainer pt-0 mb-6">
      <Col>
        <div className="d-flex flex-wrap gap-2 mb-4">
          {filters.map((filter, index) => (
            <Button
              key={index}
              className={`filter-button ${
                selectedFilter?.name === filter.name ? "selected" : ""
              }`}
              onClick={() => handleFilterClick(filter)}
            >
              <div className="filter-content ">
                <div className="filter-count">{filter.count}</div>
                <div className="filter-name">
                  {formatFilterName(filter.name)}
                </div>
              </div>
            </Button>
          ))}
        </div>
      </Col>
      <div className=" templateWrapper px-4 py-4 m-0 p-0">
        <Row className="">
          <Col xs={4} sm={4} md={4} lg={4} className="mb-3">
            <div className="d-flex w-50 mb-3">
              <Input
                value={searchQuery}
                onChange={handleSearch}
                placeholder={trans("Client.searchByName")}
                allowClear
                className="rounded-0"
              />
            </div>
          </Col>
          <Col
            xs="auto"
            className="ms-auto d-flex justify-content-end align-items-center filter-button-client"
          >
            {/* Filter Icon and Sort Dropdown */}
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                className="filter-dropdown-button"
              >
                <i className="bi bi-filter-left"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {sortMenuItems.map((item) => (
                  <Dropdown.Item
                    key={item.key}
                    onClick={() => handleSortChange({ key: item.key })}
                    className={sortOrder === item.key ? "bg-light " : ""}
                  >
                    {item.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
        {filteredData && (
          <div className="app-font-proxima custom-table-wrapper">
            <Table
              rowClassName={(record, index) => {
                if (record.status === "inactive") return "row-inactive";
                if (record.status === "active") return "row-active";
                return "clickable-row";
              }}
              className="custom-table "
              columns={columns}
              dataSource={filteredData}
              loading={loading}
              pagination={{
                className: "",
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                position: ["bottomRight"],
                showTotal: () => <Text>{getShowingText()}</Text>,
              }}
              onChange={handlePageChange}
              rowKey={(record) => record.id}
              locale={{
                emptyText: !loading && "No clients available",
              }}
              // rowClassName="clickable-row"
              onRow={(record) => ({
                onClick: () => handleClientNameClick(record),
              })}
              components={{
                table: (props) => (
                  <table
                    {...props}
                    style={{
                      ...props.style,
                      borderCollapse: "separate",
                      borderSpacing: "0 10px",
                    }}
                  />
                ),
                header: {
                  cell: (props) => {
                    return (
                      <th
                        {...props}
                        style={{
                          ...props.style,
                          background: "transparent",
                          borderColor: "#FCFBFFCC",
                          fontFamily: "Proxima-Nova-Regular",
                          borderStyle: "solid",
                          borderWidth: "8px 0px",
                          padding: "12px 16px",

                          // Border radius for header cells
                          borderRadius:
                            props.children === columns[0].title
                              ? "12px 0 0 12px"
                              : props.children ===
                                columns[columns.length - 1].title
                              ? "0 12px 12px 0"
                              : "0",
                        }}
                      />
                    );
                  },
                  row: (props) => (
                    <tr
                      {...props}
                      style={{
                        ...props.style,
                        fontFamily: "Proxima-Nova-Regular",
                        background: "transparent",
                      }}
                    />
                  ),
                },
                body: {
                  cell: (props) => (
                    <td
                      {...props}
                      style={{
                        ...props.style,
                        background: "transparent",
                        borderColor: "transparent",
                        borderStyle: "solid",
                        borderWidth: "8px 0px",
                        padding: "12px 16px",
                        fontFamily: "Proxima-Nova-Regular",
                        // Border radius for body cells
                        borderRadius:
                          props.index % columns.length === 0
                            ? "12px 0 0 12px"
                            : props.index % columns.length ===
                              columns.length - 1
                            ? "0 12px 12px 0"
                            : "0",
                      }}
                    />
                  ),
                  row: (props) => (
                    <tr
                      {...props}
                      style={{
                        ...props.style,
                        fontFamily: "Proxima-Nova-Regular",
                        background: "transparent",
                        margin: "10px 0",
                      }}
                    />
                  ),
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Client;
